import { redirectToLogin } from "../services/httpClient";
import { patientsService, PatientListOptions, RegisterPatientPayload } from "../services/patientsService";
import React, { useState, useEffect, useRef } from "react";
import HeaderInternal from "../components/Header/HeaderInternal";
import { FooterInternal } from "../components/Footer";
import { useNavigation, useRouter } from "../contexts/RouterContext";
import PatientTimeline from '../components/PatientTimeline';
import PatientOverview from '../components/PatientOverview';
import { useAuth } from "../contexts/AuthContext";
import { Visibility, VisibilityOff, CalendarToday, TrendingUp, InsertDriveFile, Person, Assessment, Note, Event, LocalHospital, Assignment, Psychology, Timeline, AttachMoney, Folder, Warning, Close, PriorityHigh, Description, Article, FileDownload, Hub, Gavel, Lock as LockIcon } from '@mui/icons-material';
import { FaqButton } from "../components/FaqButton";
import PhotoUpload from "../components/PhotoUpload";
import SavingOverlay from "../components/SavingOverlay";
import { Toast } from "../components/Toast";
import { useToast } from "../hooks/useToast";
import PatientScheduleTab from "../components/PatientScheduleTab";
import PatientNotesTab from "../components/PatientNotesTab";
import PatientLinksTab from "../components/PatientLinksTab";
import PatientDocumentsTab from "../components/PatientDocumentsTab";
import PatientContractsTab from "../components/PatientContractsTab";
import PatientFinancialTab from "../components/PatientFinancialTab";
import PatientDiagnosisTab from "../components/PatientDiagnosisTab";
import PatientAssessmentsTab from '../components/PatientAssessmentsTab';
import PatientFilesTab from '../components/PatientFilesTab';
import PatientTherapyPlanTab from '../components/PatientTherapyPlanTab';
import PatientEvolutionsTab from "../components/PatientEvolutionsTab";
import {
  Chip,
  Switch,
  TextField,
  MenuItem,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Checkbox,
  Typography,
  Box,
  CircularProgress,
  Button,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  InputAdornment
} from '@mui/material';
import { colors, typography, inputs } from '../theme/designSystem';
import { getDefaultTextFieldSx, getDefaultInputLabelProps } from '../theme/textFieldStyles';
import { isFutureBirthDate, todayIso, isValidCpf, ageFromBirthDate } from '../utils/documents';
import { titleCaseName } from '../utils/nameCase';
import { EMPTY_PHONE, PHONE_INCOMPLETE, PhoneValue, isPhoneComplete, phoneNational, phoneValueFromParts } from '../utils/phone';
import PhoneWithCountryInput from '../components/PhoneWithCountryInput';
import { useSensitiveDataAccess } from '../hooks/useSensitiveDataAccess';
import { sensitiveFieldProps } from '../components/SensitiveField';
import { SensitiveGroup } from '../services/patientSensitiveDataService';

// Máscara exibida enquanto a observação do paciente está oculta: sinaliza que há
// conteúdo sem revelá-lo em tela compartilhada durante o atendimento.
const PATIENT_NOTES_MASK = '••••••••••••••••••••';

interface MenuItemProps {
  label: string;
  href?: string;
  onClick?: (e: React.MouseEvent) => void;
}

interface PatientFormData {
  id?: string;
  name: string;
  isResponsible: boolean;
  phone: PhoneValue;
  cep: string;
  documentType: string;
  document: string;
  email: string;
  responsibleName: string;
  address: string;
  number: string;
  expeditorOrgan: string;
  birthDate: string;
  responsibleDocumentType: string;
  responsibleDocument: string;
  complement: string;
  neighborhood: string;
  gender: string;
  kinship: string;
  responsiblePhone: PhoneValue;
  city: string;
  uf: string;
  nativeLanguage: string;
  originCountry: string;
  responsibleEmail: string;
  isComplete: boolean;
  observations: string;
  /** observações escritas pelo paciente/responsável no formulário público (RN-112i) */
  patientNotes: string;
  referredBy: string;
  entryChannel: string;
  photo?: string;
  photoRotation?: number;
  photoZoom?: number;
  photoFlipX?: number;
  photoPositionX?: number;
  photoPositionY?: number;
  responsible2Name: string;
  responsible2DocumentType: string;
  responsible2Document: string;
  responsible2Kinship: string;
  responsible2Phone: PhoneValue;
  responsible2Email: string;
  responsibleFinancial: boolean;
  responsible2Financial: boolean;
}

// Abas já integradas à base. As demais seguem mockup, então a Visão geral não oferece atalho
// para elas: mandar a pessoa para uma tela que finge funcionar é pior do que não ter o link.
const REAL_TABS = ['cadastro', 'linha-do-tempo', 'agenda', 'anotacoes', 'diagnostico', 'avaliacoes', 'evolucoes', 'vinculos', 'contratos'];

// Abas do cadastro que exigem permissão própria (RN-527). O valor é o `internal_page` da
// funcionalidade correspondente; aba ausente deste mapa não é controlada.
const TAB_PERMISSIONS: Record<string, string> = {
  resumo: 'patient-overview',
  'linha-do-tempo': 'patient-timeline',
  agenda: 'schedule',
  diagnostico: 'patient-diagnosis',
  avaliacoes: 'patient-assessments',
  evolucoes: 'patient-evolutions',
  financeiro: 'patient-financial',
  receituario: 'patient-prescriptions',
  // Secretário anexa documento, então vê Arquivos — e segue a régua da RN-540: abre o que
  // anexou, do resto vê só que existe. Plano Terapêutico é de Profissional e Administrador.
  arquivos: 'patient-files',
  'plano-terap': 'patient-therapy-plan',
  // Vínculos (RN-553): escola, trabalho, instituição — não é dado clínico, e é a Secretaria
  // que liga para a escola pedir o relatório.
  vinculos: 'patient-links',
  // Contratos (RN-558): quem vê a aba; emitir e cancelar têm permissões-filhas lidas dentro da aba
  contratos: 'patient-contracts',
  // Só quem administra o acesso enxerga a aba (RN-540a): é do Administrador
  'acesso-dados': 'patient-data-access',
};

const PatientRegister: React.FC = () => {
  const { goToPatients, goToDashboard } = useNavigation();
  const { getParam } = useRouter();
  const { user } = useAuth();
  // Criar e ABRIR o prontuario sao permissoes diferentes (RN-112p) -- a tela e a mesma.
  const podeCriarPaciente = (user?.allowedPages || []).includes('patient-create');
  const { toast, showToast, hideToast } = useToast();
  const { restricted: sensitiveRestricted } = useSensitiveDataAccess();
  // Paciente de teste (RN-552): o selo todo mundo vê; ligar/desligar exige a permissão (o backend
  // também exige). Item 163: era comparação com o NOME do perfil, que sumia se a clínica renomeasse.
  const canSetTestFlag = (user?.allowedPages || []).includes('patient-test-flag');
  const [isTestPatient, setIsTestPatient] = useState(false);
  const [savingTestFlag, setSavingTestFlag] = useState(false);
  const toggleTestFlag = async (next: boolean) => {
    if (!formData.id || savingTestFlag) return;
    setSavingTestFlag(true);
    try {
      await patientsService.setTestFlag(formData.id, next);
      setIsTestPatient(next);
      showToast(next ? 'Paciente marcado como teste. Ele sai dos relatórios e dos recebimentos.' : 'Marca de teste removida.', 'success');
    } catch (e: unknown) {
      const data = (e as { response?: { data?: { description?: string } } })?.response?.data;
      showToast(data?.description || 'Não foi possível alterar a marca de teste.', 'error');
    } finally {
      setSavingTestFlag(false);
    }
  };


  // Reusable styles for form fields
  const textFieldSx = {
    '& .MuiOutlinedInput-root': {
      height: '40px',
      fontSize: '1rem',
      backgroundColor: 'white',
      '& fieldset': {
        borderColor: '#ced4da',
        legend: { maxWidth: '100%' },
      },
    },
    '& .MuiSelect-select': {
      padding: '0.375rem 0.5rem',
      color: '#495057',
    },
    '& .MuiInputLabel-root': {
      fontSize: '0.95rem',
      color: '#6c757d',
      backgroundColor: 'white',
      paddingLeft: '4px',
      paddingRight: '4px',
      '&.Mui-focused': {
        color: '#03B4C6',
      },
    },
  };

  const [formData, setFormData] = useState<PatientFormData>({
    name: "",
    isResponsible: false,
    phone: EMPTY_PHONE,
    cep: "",
    documentType: "CPF",
    document: "",
    email: "",
    responsibleName: "",
    address: "",
    number: "",
    expeditorOrgan: "",
    birthDate: "",
    responsibleDocumentType: "CPF",
    responsibleDocument: "",
    complement: "",
    neighborhood: "",
    gender: "",
    kinship: "",
    responsiblePhone: EMPTY_PHONE,
    city: "",
    uf: "",
    nativeLanguage: "Português",
    originCountry: "Brasil",
    responsibleEmail: "",
    isComplete: false,
    observations: "",
    patientNotes: "",
    referredBy: "",
    entryChannel: "",
    responsible2Name: "",
    responsible2DocumentType: "CPF",
    responsible2Document: "",
    responsible2Kinship: "",
    responsible2Phone: EMPTY_PHONE,
    responsible2Email: "",
    responsibleFinancial: false,
    responsible2Financial: false
  });
  const [isLoading, setIsLoading] = useState(false);
  // ?tab= abre direto numa aba (ex.: o caixa abre a Agenda num agendamento)
  const [activeTab, setActiveTab] = useState(getParam('tab') || 'cadastro');
  const [isNewPatient, setIsNewPatient] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [cepLoading, setCepLoading] = useState(false);
  const [originalFormData, setOriginalFormData] = useState<PatientFormData | null>(null);
  // Catálogos do tenant (gêneros e tipos de documento) — alimentam os selects do cadastro
  const [listOptions, setListOptions] = useState<PatientListOptions | null>(null);
  // Tipo de documento e país do paciente carregado (ids do catálogo) — resolvidos para
  // o nome quando os catálogos chegam (as cargas são assíncronas e independentes)
  const [loadedDocTypeId, setLoadedDocTypeId] = useState<string | null>(null);
  const [loadedCountryId, setLoadedCountryId] = useState<string | null>(null);
  // Observação do paciente fica oculta por padrão (o "olho" revela) — evita expor
  // o relato do paciente em tela compartilhada durante o atendimento.
  const [showPatientNotes, setShowPatientNotes] = useState(false);
  const hasPatientNotes = Boolean(formData.patientNotes?.trim());

  // Foto (RN-113a): recurso com save PRÓPRIO, independente do formulário. Embaça por
  // padrão (privacidade); marca "dirty" para habilitar o botão só quando há mudança.
  const [photoBlurred, setPhotoBlurred] = useState(true);
  // Olho de privacidade (item 130): oculta os dados da aba Cadastro na TELA, deixando só o nome.
  // É estado de tela, não de perfil — o bloqueio do perfil (RN-112q) prevalece porque o dado nem chega.
  const [privacyHidden, setPrivacyHidden] = useState(true);
  const [isSavingPhoto, setIsSavingPhoto] = useState(false);
  const [photoDirty, setPhotoDirty] = useState(false);
  const [serverHasPhoto, setServerHasPhoto] = useState(false);
  // object URL da foto carregada (blob) — revogar ao desmontar para não vazar memória.
  const photoObjectUrlRef = useRef<string | null>(null);

  useEffect(() => () => {
    if (photoObjectUrlRef.current) {
      URL.revokeObjectURL(photoObjectUrlRef.current);
    }
  }, []);

  // Estados dos modais de avaliação
  const [isEvaluationModalOpen, setIsEvaluationModalOpen] = useState(false);
  const [isDeleteEvaluationModalOpen, setIsDeleteEvaluationModalOpen] = useState(false);
  const [currentEvaluation, setCurrentEvaluation] = useState<any>(null);
  const [evaluationFormData, setEvaluationFormData] = useState({
    form: '',
    observations: '',
    deadline: ''
  });

  // Lista mock de formulários de avaliação
  const formList = [
    'Avaliação Cardiológica Inicial',
    'Avaliação Neurológica',
    'Avaliação Ortopédica',
    'Avaliação Psicológica',
    'Avaliação Nutricional',
    'Avaliação Fisioterapêutica',
    'Avaliação Pediátrica',
    'Avaliação Geriátrica',
  ];

  // Estados dos filtros de avaliações
  const [evalTypeFilter, setEvalTypeFilter] = useState('');
  const [evalStatusFilter, setEvalStatusFilter] = useState('');
  const [evalStartDate, setEvalStartDate] = useState('');
  const [evalEndDate, setEvalEndDate] = useState('');
  const [evalRequestedByFilter, setEvalRequestedByFilter] = useState('');

  // Lista mock de avaliações
  const [evaluationsList, setEvaluationsList] = useState([
    {
      id: '1',
      form: 'Avaliação Cardiológica Inicial',
      type: 'inicial',
      observations: 'Paciente apresenta histórico familiar de problemas cardíacos',
      deadline: '2025-10-15',
      createdDate: '2025-10-01',
      completionPercentage: 100,
      status: 'Finalizada',
      requestedBy: 'dr_silva'
    },
    {
      id: '2',
      form: 'Avaliação Nutricional',
      type: 'inicial',
      observations: 'Necessário avaliar hábitos alimentares e orientar sobre dieta balanceada',
      deadline: '2025-10-20',
      createdDate: '2025-10-03',
      completionPercentage: 75,
      status: 'Em andamento',
      requestedBy: 'dra_oliveira'
    },
    {
      id: '3',
      form: 'Avaliação Psicológica',
      type: 'especializada',
      observations: 'Paciente relata ansiedade e estresse relacionado ao trabalho',
      deadline: '2025-10-18',
      createdDate: '2025-10-02',
      completionPercentage: 50,
      status: 'Em andamento',
      requestedBy: 'dr_santos'
    },
    {
      id: '4',
      form: 'Avaliação Fisioterapêutica',
      type: 'reavaliacao',
      observations: 'Avaliar mobilidade e recomendar exercícios para fortalecimento',
      deadline: '2025-10-25',
      createdDate: '2025-10-05',
      completionPercentage: 30,
      status: 'Em andamento',
      requestedBy: 'dra_costa'
    },
    {
      id: '5',
      form: 'Avaliação Ortopédica',
      type: 'inicial',
      observations: 'Paciente queixa-se de dores na coluna lombar',
      deadline: '2025-10-12',
      createdDate: '2025-09-28',
      completionPercentage: 100,
      status: 'Finalizada',
      requestedBy: 'dr_silva'
    },
    {
      id: '6',
      form: 'Avaliação Neurológica',
      type: 'especializada',
      observations: 'Avaliar episódios de enxaqueca recorrente',
      deadline: '2025-10-22',
      createdDate: '2025-10-04',
      completionPercentage: 15,
      status: 'Em andamento',
      requestedBy: 'dra_oliveira'
    },
    {
      id: '7',
      form: 'Avaliação Geriátrica',
      type: 'inicial',
      observations: 'Avaliação preventiva de saúde do idoso',
      deadline: '2025-10-30',
      createdDate: '2025-10-06',
      completionPercentage: 0,
      status: 'Pendente',
      requestedBy: 'dr_santos'
    },
    {
      id: '8',
      form: 'Avaliação Pediátrica',
      type: 'reavaliacao',
      observations: 'Acompanhamento de desenvolvimento motor e cognitivo',
      deadline: '2025-10-28',
      createdDate: '2025-10-05',
      completionPercentage: 100,
      status: 'Finalizada',
      requestedBy: 'dra_costa'
    }
  ]);

  // Função para limpar filtros de avaliações
  const handleClearEvaluationsFilters = () => {
    setEvalTypeFilter('');
    setEvalStatusFilter('');
    setEvalStartDate('');
    setEvalEndDate('');
    setEvalRequestedByFilter('');
  };

  // Função para filtrar avaliações
  const filteredEvaluations = evaluationsList.filter((evaluation) => {
    // Filtro por tipo
    if (evalTypeFilter && evaluation.type !== evalTypeFilter) {
      return false;
    }

    // Filtro por status
    if (evalStatusFilter) {
      const statusMap: { [key: string]: string } = {
        'concluida': 'Finalizada',
        'pendente': 'Pendente',
        'em_andamento': 'Em andamento'
      };
      if (evaluation.status !== statusMap[evalStatusFilter]) {
        return false;
      }
    }

    // Filtro por data inicial
    if (evalStartDate) {
      const evalDate = new Date(evaluation.createdDate);
      const filterDate = new Date(evalStartDate);
      if (evalDate < filterDate) {
        return false;
      }
    }

    // Filtro por data final
    if (evalEndDate) {
      const evalDate = new Date(evaluation.createdDate);
      const filterDate = new Date(evalEndDate);
      if (evalDate > filterDate) {
        return false;
      }
    }

    // Filtro por solicitante
    if (evalRequestedByFilter && evaluation.requestedBy !== evalRequestedByFilter) {
      return false;
    }

    return true;
  });

  // Funções para gerenciar anotações
  // Funções para gerenciar diagnósticos
  // Funções dos modais de avaliação
  const handleOpenEvaluationModal = (evaluation?: any) => {
    if (evaluation) {
      setCurrentEvaluation(evaluation);
      setEvaluationFormData({
        form: evaluation.form || '',
        observations: evaluation.observations || '',
        deadline: evaluation.deadline || ''
      });
    } else {
      setCurrentEvaluation(null);
      setEvaluationFormData({
        form: '',
        observations: '',
        deadline: ''
      });
    }
    setIsEvaluationModalOpen(true);
  };

  const handleCloseEvaluationModal = () => {
    setIsEvaluationModalOpen(false);
    setCurrentEvaluation(null);
    setEvaluationFormData({
      form: '',
      observations: '',
      deadline: ''
    });
  };

  const handleSaveEvaluation = () => {
    if (currentEvaluation) {
      // Editar avaliação existente
      setEvaluationsList(evaluationsList.map(evaluation =>
        evaluation.id === currentEvaluation.id
          ? { ...evaluation, observations: evaluationFormData.observations, deadline: evaluationFormData.deadline }
          : evaluation
      ));
    } else {
      // Criar nova avaliação
      const newEvaluation = {
        id: `${Date.now()}`,
        form: evaluationFormData.form,
        type: 'inicial',
        observations: evaluationFormData.observations,
        deadline: evaluationFormData.deadline,
        createdDate: new Date().toISOString().split('T')[0],
        completionPercentage: 0,
        status: 'Pendente',
        requestedBy: 'dr_silva'
      };
      setEvaluationsList([...evaluationsList, newEvaluation]);
    }
    handleCloseEvaluationModal();
  };

  const handleOpenDeleteEvaluationModal = (evaluation: any) => {
    setCurrentEvaluation(evaluation);
    setIsDeleteEvaluationModalOpen(true);
  };

  const handleCloseDeleteEvaluationModal = () => {
    setIsDeleteEvaluationModalOpen(false);
    setCurrentEvaluation(null);
  };

  const handleDeleteEvaluation = () => {
    setEvaluationsList(evaluationsList.filter(evaluation => evaluation.id !== currentEvaluation.id));
    handleCloseDeleteEvaluationModal();
  };

  // Filtrar CIDs baseado no texto de busca
  // Limpar campo "Indicado por" quando canal não for indicação
  useEffect(() => {
    if (formData.entryChannel && !formData.entryChannel.includes('Indicação')) {
      setFormData(prev => ({ ...prev, referredBy: '' }));
    }
  }, [formData.entryChannel]);

  // Resolve tipo de documento e país do paciente carregado quando o catálogo chega
  useEffect(() => {
    if (!listOptions) return;
    const docType = loadedDocTypeId
      ? listOptions.documentTypes.find(d => d.id === loadedDocTypeId)
      : undefined;
    const country = loadedCountryId
      ? (listOptions.countries || []).find(c => c.id === loadedCountryId)
      : undefined;
    if (!docType && !country) return;
    const patch = {
      ...(docType ? { documentType: docType.name } : {}),
      ...(country ? { originCountry: country.name } : {}),
    };
    setFormData(prev => ({ ...prev, ...patch }));
    setOriginalFormData(prev => (prev ? { ...prev, ...patch } : prev));
  }, [loadedDocTypeId, loadedCountryId, listOptions]);

  // Limpar órgão expedidor quando o tipo de documento não o exige (ex.: trocar RG -> CPF).
  // Sem o catálogo carregado não dá para afirmar a exigência — não limpa nada.
  useEffect(() => {
    if (!listOptions) return;
    const requiresExpedition =
      listOptions.documentTypes.find(d => d.name === formData.documentType)?.requiresExpedition ?? false;
    if (!requiresExpedition) {
      setFormData(prev => (prev.expeditorOrgan ? { ...prev, expeditorOrgan: '' } : prev));
    }
  }, [formData.documentType, listOptions]);

  // O cabeçalho usa a sessão REAL (AuthContext). Até aqui havia uma "sessão simulada" com
  // user@clinic4us.com / Clínica Demo, resíduo do mockup: quem abria o cadastro via dados de
  // demonstração no menu lateral, mesmo logado com a própria conta.
  useEffect(() => {
    document.title = "Clinic4Us - Cadastro de Paciente";
  }, []);

  // Catálogos para os selects (sexo biológico e tipo de documento)
  useEffect(() => {
    patientsService.getListOptions()
      .then(setListOptions)
      .catch((error) => console.error('Erro ao carregar opções do cadastro:', error));
  }, []);

  // Carregar dados do paciente se houver ID na URL.
  // O efeito depende do VALOR do id, não de getParam/goToPatients: essas funções são recriadas a
  // cada render e, como dependência, faziam o efeito rodar em laço (recarregar -> setState ->
  // render -> recarregar), dezenas de GET /patients/{id} por segundo em prd (13/09/2026).
  const patientIdParam = getParam('id');
  useEffect(() => {
    const patientId = patientIdParam;

    if (patientId) {
      // Paciente existente - modo visualização, preenchido com os dados reais (RN-112d)
      setIsNewPatient(false);
      setIsEditing(false);

      patientsService.getById(patientId)
        .then((p) => {
          setIsTestPatient(!!p.isTest);
          const loaded: PatientFormData = {
            id: p.id,
            name: titleCaseName(p.patientName),
            isResponsible: p.patientIsResponsible,
            phone: phoneValueFromParts(p.patientPhoneDdi, p.patientPhoneCode, p.patientPhone),
            cep: p.addressZipCode || "",
            documentType: "CPF",
            document: p.patientDocument || "",
            email: p.patientEmail || "",
            responsibleName: titleCaseName(p.responsibleName1),
            address: p.addressStreet || "",
            number: p.addressNumber || "",
            expeditorOrgan: "",
            birthDate: p.patientBirthDate ? p.patientBirthDate.substring(0, 10) : "",
            responsibleDocumentType: "CPF",
            responsibleDocument: p.responsibleDocument1 || "",
            complement: p.addressComplement || "",
            neighborhood: p.addressDistrict || "",
            gender: p.genderName || "",
            kinship: "",
            responsiblePhone: phoneValueFromParts(p.responsiblePhoneDdi1, p.responsiblePhoneCode1, p.responsiblePhone1),
            city: p.addressCity || "",
            uf: p.addressState || "",
            nativeLanguage: p.patientNativeLanguage || "Português",
            originCountry: "Brasil",
            responsibleEmail: p.responsibleEmail1 || "",
            isComplete: p.completeRegister,
            observations: p.notes || "",
            patientNotes: p.patientNotes || "",
            referredBy: p.referredBy || "",
            entryChannel: p.registerOrigin || "",
            photo: undefined,
            photoRotation: 0,
            photoZoom: 1,
            photoFlipX: 1,
            photoPositionX: 0,
            photoPositionY: 0,
            responsible2Name: titleCaseName(p.responsibleName2),
            responsible2DocumentType: "CPF",
            responsible2Document: p.responsibleDocument2 || "",
            responsible2Kinship: "",
            responsible2Phone: phoneValueFromParts(p.responsiblePhoneDdi2, p.responsiblePhoneCode2, p.responsiblePhone2),
            responsible2Email: p.responsibleEmail2 || "",
            responsibleFinancial: false,
            responsible2Financial: false
          };

          setFormData(loaded);
          setOriginalFormData(loaded);
          setLoadedDocTypeId(p.patientDocumentType);
          setLoadedCountryId(p.patientCountry);

          // Foto (RN-113a) é recurso à parte: se existir, busca autenticada (blob) e injeta.
          setServerHasPhoto(p.hasPhoto);
          setPhotoDirty(false);
          setPhotoBlurred(true);
          if (p.hasPhoto) {
            patientsService.getPhoto(patientId)
              .then((url) => {
                photoObjectUrlRef.current = url;
                setFormData(prev => ({ ...prev, photo: url }));
                setOriginalFormData(prev => (prev ? { ...prev, photo: url } : prev));
              })
              .catch((error) => console.error('Erro ao carregar foto do paciente:', error));
          }
        })
        .catch((error) => {
          console.error('Erro ao carregar paciente:', error);
        });
    } else {
      // Novo paciente. Sem `patient-create` a tela nao abre em modo de inclusao (RN-112p): a URL
      // e a mesma que abre o prontuario, entao quem so consulta chegaria aqui digitando o endereco
      // sem o `?id=`. O servidor tambem recusa a criacao -- esta guarda evita o formulario inutil.
      if (!podeCriarPaciente) {
        goToPatients();
        return;
      }
      setIsNewPatient(true);
      setIsEditing(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientIdParam, podeCriarPaciente]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    let processedValue: any = value;

    // Handle boolean fields that come as strings from radio buttons
    if (name === 'isResponsible' || name === 'isComplete') {
      processedValue = value === 'true';
    } else if (type === 'checkbox') {
      processedValue = checked;
    }

    // Handle mutual exclusion for financial responsible checkboxes
    if (name === 'responsibleFinancial' || name === 'responsible2Financial') {
      if (checked) {
        // If checking one, uncheck the other
        setFormData(prev => ({
          ...prev,
          [name]: true,
          // Uncheck the other financial responsible
          ...(name === 'responsibleFinancial'
            ? { responsible2Financial: false }
            : { responsibleFinancial: false })
        }));
      } else {
        // If unchecking, just update the current field
        setFormData(prev => ({
          ...prev,
          [name]: false
        }));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: processedValue
      }));
    }

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const changePhone = (field: 'phone' | 'responsiblePhone' | 'responsible2Phone') => (value: PhoneValue) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const calculateAge = (birthDate: string): string => {
    if (!birthDate) return '';

    const birth = new Date(birthDate);
    const today = new Date();

    // Validação: data de nascimento não pode ser maior que hoje
    if (birth > today) {
      return 'Data inválida';
    }

    let years = today.getFullYear() - birth.getFullYear();
    let months = today.getMonth() - birth.getMonth();
    let days = today.getDate() - birth.getDate();

    if (days < 0) {
      months--;
      const lastMonth = new Date(today.getFullYear(), today.getMonth(), 0);
      days += lastMonth.getDate();
    }

    if (months < 0) {
      years--;
      months += 12;
    }

    const parts = [];
    if (years > 0) parts.push(`${years} ano${years !== 1 ? 's' : ''}`);
    if (months > 0) parts.push(`${months} mês${months !== 1 ? 'es' : ''}`);
    if (days > 0) parts.push(`${days} dia${days !== 1 ? 's' : ''}`);

    return parts.length > 0 ? parts.join(', ') : '0 dias';
  };

  const formatCep = (value: string) => {
    const numbers = value.replace(/\D/g, '');
    return numbers.replace(/(\d{5})(\d{3})/, '$1-$2');
  };

  const handleCepSearch = async (cep: string) => {
    const cleanCep = cep.replace(/\D/g, '');

    if (cleanCep.length !== 8) {
      return;
    }

    setCepLoading(true);

    try {
      const response = await fetch(`https://viacep.com.br/ws/${cleanCep}/json/`);
      const data = await response.json();

      if (!data.erro) {
        setFormData(prev => ({
          ...prev,
          address: data.logradouro || '',
          neighborhood: data.bairro || '',
          city: data.localidade || '',
          uf: data.uf || ''
        }));
      } else {
        alert('CEP não encontrado. Verifique o código digitado.');
      }
    } catch (error) {
      alert('Erro ao buscar CEP. Tente novamente.');
    } finally {
      setCepLoading(false);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: typeof errors = {};
    // Erros sem campo próprio acumulam aqui (exibidos juntos acima dos botões)
    const generalErrors: string[] = [];

    if (!formData.name.trim()) {
      newErrors.name = "Nome é obrigatório";
    }

    // Documento é opcional (alinhado ao cadastro rápido e ao formulário por email);
    // quando preenchido, valida pela régua do tipo selecionado no catálogo
    if ((isNewPatient || !sensitiveRestricted('documents')) && formData.document.trim()) {
      const docType = listOptions?.documentTypes.find(d => d.name === formData.documentType);
      const digits = formData.document.replace(/\D/g, '');
      const isCpfType = !docType || docType.name.toUpperCase() === 'CPF';
      if (isCpfType && !isValidCpf(formData.document)) {
        newErrors.document = "CPF inválido. Confira os números informados";
      } else if (!isCpfType && docType &&
        (digits.length < (docType.minLength ?? 1) || digits.length > (docType.maxLength ?? 20))) {
        newErrors.document = `Documento deve ter entre ${docType.minLength ?? 1} e ${docType.maxLength ?? 20} caracteres`;
      }
    }

    if (!formData.birthDate) {
      newErrors.birthDate = "Data de nascimento é obrigatória";
    } else if (isFutureBirthDate(formData.birthDate)) {
      newErrors.birthDate = "A data não pode ser posterior a hoje";
    }

    if (formData.isResponsible === undefined || formData.isResponsible === null) {
      newErrors.isResponsible = "Deve informar se é o próprio responsável";
    }

    // Validação de contato - obrigatório se for o próprio responsável.
    // Telefone/e-mail bloqueados pelo perfil não se validam: o servidor preserva o que já existe.
    const contactHidden = !isNewPatient && (sensitiveRestricted('phones') || sensitiveRestricted('emails'));
    if (contactHidden) {
      // sem validação de contato
    } else if (formData.isResponsible) {
      if (!phoneNational(formData.phone)) {
        newErrors.phone = "Telefone é obrigatório quando é o próprio responsável";
      } else if (!isPhoneComplete(formData.phone)) {
        newErrors.phone = PHONE_INCOMPLETE;
      }

      if (!formData.email.trim()) {
        newErrors.email = "Email é obrigatório quando é o próprio responsável";
      } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
        newErrors.email = "Email deve ter um formato válido";
      }
    } else {
      // Validação opcional se não for o próprio responsável
      if (phoneNational(formData.phone) && !isPhoneComplete(formData.phone)) {
        newErrors.phone = PHONE_INCOMPLETE;
      }

      if (formData.email.trim() && !/\S+@\S+\.\S+/.test(formData.email)) {
        newErrors.email = "Email deve ter um formato válido";
      }

      // Sem contato do paciente, o do responsável vira obrigatório (regra do backend)
      const patientContactOk = Boolean(phoneNational(formData.phone) && formData.email.trim());
      const responsibleContactOk = Boolean(phoneNational(formData.responsiblePhone) && formData.responsibleEmail.trim());
      if (!patientContactOk && !responsibleContactOk) {
        generalErrors.push("Informe telefone e email de contato — do paciente ou do responsável.");
      }
    }

    // Menor de 18 anos exige o nome do responsável (mesma régua do backend)
    const age = ageFromBirthDate(formData.birthDate);
    if (age !== null && age < 18 && !formData.responsibleName.trim()) {
      generalErrors.push("Paciente menor de 18 anos: informe o nome do responsável.");
    }

    // Responsável financeiro é gravado pelo CPF (campo legado) — sem CPF a marcação se perderia
    if ((isNewPatient || !sensitiveRestricted('documents')) && formData.responsibleFinancial && !formData.responsibleDocument.trim()) {
      generalErrors.push("Informe o CPF do responsável marcado como financeiro.");
    }
    if ((isNewPatient || !sensitiveRestricted('documents')) && formData.responsible2Financial && !formData.responsible2Document.trim()) {
      generalErrors.push("Informe o CPF do segundo responsável marcado como financeiro.");
    }

    if (generalErrors.length > 0) {
      newErrors.general = generalErrors.join(" ");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      const gender = listOptions?.genders.find(g => g.name === formData.gender);
      const docType = listOptions?.documentTypes.find(d => d.name === formData.documentType);
      const country = (listOptions?.countries || []).find(c => c.name === formData.originCountry);
      const clean = (value: string): string | null => {
        const trimmed = (value || '').trim();
        return trimmed ? trimmed : null;
      };

      const payload: RegisterPatientPayload = {
        patientName: formData.name.trim(),
        birthDate: formData.birthDate || null,
        genderId: gender?.id ?? null,
        documentTypeId: docType?.id ?? null,
        document: clean(formData.document),
        documentExpedition: docType?.requiresExpedition ? clean(formData.expeditorOrgan) : null,
        phone: phoneNational(formData.phone),
        phoneDdi: formData.phone.ddi,
        email: clean(formData.email),
        countryId: country?.id ?? null,
        nativeLanguage: clean(formData.nativeLanguage),
        patientIsResponsible: formData.isResponsible === true,
        responsibleName1: clean(formData.responsibleName),
        responsibleDocument1: clean(formData.responsibleDocument),
        responsiblePhone1: phoneNational(formData.responsiblePhone),
        responsiblePhoneDdi1: formData.responsiblePhone.ddi,
        responsibleEmail1: clean(formData.responsibleEmail),
        responsibleName2: clean(formData.responsible2Name),
        responsibleDocument2: clean(formData.responsible2Document),
        responsiblePhone2: phoneNational(formData.responsible2Phone),
        responsiblePhoneDdi2: formData.responsible2Phone.ddi,
        responsibleEmail2: clean(formData.responsible2Email),
        financialResponsible: formData.responsibleFinancial ? 1 : (formData.responsible2Financial ? 2 : null),
        addressZipCode: clean(formData.cep),
        addressStreet: clean(formData.address),
        addressNumber: clean(formData.number),
        addressComplement: clean(formData.complement),
        addressDistrict: clean(formData.neighborhood),
        addressCity: clean(formData.city),
        addressState: clean(formData.uf),
        notes: clean(formData.observations),
        registerOrigin: clean(formData.entryChannel),
        referredBy: clean(formData.referredBy),
        completeRegister: formData.isComplete === true,
      };

      if (!isNewPatient && formData.id) {
        // Edição (RN-112d): a API grava no Bubble (master) e no tenant; a tela volta ao modo
        // visualização com o que foi salvo como novo "original" (o Cancelar de uma próxima edição
        // volta para este estado, não para o de antes do save).
        await patientsService.update(formData.id, payload);
        setOriginalFormData({ ...formData });
        setIsEditing(false);
        setErrors({});
        return;
      }

      const newId = await patientsService.register(payload);
      // Recarrega a própria página com o id (mesmo padrão do cadastro rápido):
      // modo visualização, foto e demais abas liberadas
      window.location.assign(`${window.location.origin}${window.location.pathname}?page=patient-register&id=${newId}`);
    } catch (err: any) {
      setErrors({ general: err?.response?.data?.description || "Erro ao salvar paciente. Tente novamente." });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    if (isNewPatient) {
      goToPatients();
    } else {
      // Cancelar edição - restaurar dados originais
      if (originalFormData) {
        setFormData(originalFormData);
      }
      setIsEditing(false);
      setErrors({});
    }
  };

  const handleEdit = () => {
    // Salvar os dados atuais antes de editar
    setOriginalFormData({ ...formData });
    setPrivacyHidden(false);
    setIsEditing(true);

    // Focar no primeiro campo após um pequeno delay para garantir que o campo foi habilitado
    setTimeout(() => {
      const nameInput = document.getElementById('name');
      if (nameInput) {
        nameInput.focus();
        nameInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
  };

  // Determina se os campos podem ser editados
  const canEdit = isNewPatient || isEditing;
  // RN-112q: grupo bloqueado para o perfil chega nulo da API; o campo vira cadeado + "Restrito",
  // não entra na validação e volta vazio no salvar — o servidor preserva o valor atual.
  // Paciente NOVO não tem dado alheio a proteger: quem cadastra informa tudo; o bloqueio vale depois que o cadastro existe.
  const sensitiveLock = (group: SensitiveGroup) => (!isNewPatient && sensitiveRestricted(group) ? sensitiveFieldProps() : {});
  const canEditRegister = !sensitiveRestricted('editRegister');
  // Item 130: com o olho fechado todo campo mostra ******** (paciente novo não tem olho).
  const privacyOn = !isNewPatient && privacyHidden;
  const privacyMask = () => (privacyOn ? { value: '********', type: 'text', disabled: true, placeholder: '', error: false, helperText: undefined, InputProps: {} } : {});
  // Telefone mascarado (olho fechado ou grupo bloqueado no perfil): um campo só, como os demais.
  const phoneLock = () => {
    const locked = { ...privacyMask(), ...sensitiveLock('phones') };
    return Object.keys(locked).length > 0 ? locked : undefined;
  };
  const privacyMaskSelect = () => (privacyOn ? { value: '', disabled: true, SelectProps: { displayEmpty: true, renderValue: () => '********' } } : {});

  // Órgão expedidor só se aplica a tipos com essa exigência no catálogo (RG; CPF não)
  const docTypeRequiresExpedition =
    listOptions?.documentTypes.find(d => d.name === formData.documentType)?.requiresExpedition ?? false;

  const getAvailableTabs = () => {
    // Cadastro novo: as demais abas dependem de um paciente existente — só Cadastro (RN-112l)
    if (isNewPatient) {
      return [{ id: 'cadastro', label: 'Cadastro', enabled: true, icon: Person, color: '#03B4C6' }];
    }

    // Ordem por importância no dia a dia (Hilton, 07/09/2026): as cinco primeiras são as que ele
    // ditou; depois vem o registro clínico de apoio (Diagnóstico, Linha do tempo, Anotações),
    // a operação (Agenda, Financeiro) e por fim o que é consulta ocasional (Documentos, Arquivos).
    const allTabs = [
      { id: 'cadastro', label: 'Cadastro', enabled: true, icon: Person, color: '#03B4C6' },
      { id: 'resumo', label: 'Visão geral', enabled: true, icon: Assessment, color: '#2196f3' },
      { id: 'plano-terap', label: 'Plano Terap', enabled: true, icon: Psychology, color: '#e91e63' },
      { id: 'evolucoes', label: 'Evoluções', enabled: true, icon: Timeline, color: '#00bcd4' },
      { id: 'avaliacoes', label: 'Avaliações', enabled: true, icon: Assignment, color: '#4caf50' },
      { id: 'diagnostico', label: 'Diagnóstico', enabled: true, icon: LocalHospital, color: '#f44336' },
      { id: 'linha-do-tempo', label: 'Linha do tempo', enabled: true, icon: Timeline, color: '#0d6e6e' },
      { id: 'anotacoes', label: 'Anotações', enabled: true, icon: Note, color: '#ff9800' },
      { id: 'vinculos', label: 'Vínculos', enabled: true, icon: Hub, color: '#607d8b' },
      { id: 'agenda', label: 'Agenda', enabled: true, icon: Event, color: '#9c27b0' },
      { id: 'financeiro', label: 'Financeiro', enabled: true, icon: AttachMoney, color: '#4caf50' },
      { id: 'receituario', label: 'Documentos', enabled: true, icon: Description, color: '#009688' },
      { id: 'contratos', label: 'Contratos', enabled: true, icon: Gavel, color: '#5c6bc0' },
      { id: 'arquivos', label: 'Arquivos', enabled: true, icon: Folder, color: '#795548' }
      // Acesso a dados (RN-540a) mora na Visão geral desde 04/09/2026 — era a mesma
      // informação do bloco de supervisão, com dois nomes (item 51).
    ];

    // Permissão POR ABA (RN-527): cada aba controlada é uma funcionalidade página-filha, então
    // reaproveita o RBAC que já existe — `allowedPages` chega no login. Aba fora do mapa continua
    // visível, o que permite controlar uma de cada vez sem derrubar as demais.
    // Antes daqui, todas as regras terminavam em `|| true` ("Allow for demo") e qualquer perfil
    // que abrisse um paciente enxergava Financeiro e Receituário.
    const allowedPages = user?.allowedPages || [];
    return allTabs.filter(tab => {
      const requiredPage = TAB_PERMISSIONS[tab.id];
      return requiredPage ? allowedPages.includes(requiredPage) : true;
    });
  };

  const handleTabChange = (tabId: string) => {
    const availableTabs = getAvailableTabs();
    const tab = availableTabs.find(t => t.id === tabId);
    if (tab && tab.enabled) {
      setActiveTab(tabId);
    }
  };

  if (!user) {
    return <div>Carregando...</div>;
  }


  const handleRevalidateLogin = () => {
    localStorage.removeItem('clinic4us-user-session');
    localStorage.removeItem('clinic4us-remember-me');
    alert("Sessão encerrada. Redirecionando para login..");
    redirectToLogin();
  };

  const handleNotificationClick = () => {
    alert("Sistema de notificações - 27 notificações pendentes");
  };

  const handleUserClick = () => {
    alert("Configurações do usuário - funcionalidade em desenvolvimento");
  };

  const handleLogoClick = () => {
    goToDashboard();
  };

  // Renderiza a foto recortada (mesmas transformações do preview) como Blob JPEG p/ upload.
  const createCroppedPhotoBlob = (): Promise<Blob | null> => {
    return new Promise((resolve) => {
      if (!formData.photo) {
        resolve(null);
        return;
      }
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        resolve(null);
        return;
      }
      const img = new Image();
      img.onload = () => {
        canvas.width = 250;
        canvas.height = 250;
        ctx.save();
        ctx.translate(canvas.width / 2, canvas.height / 2);
        ctx.translate(formData.photoPositionX || 0, formData.photoPositionY || 0);
        ctx.rotate((formData.photoRotation || 0) * Math.PI / 180);
        ctx.scale(formData.photoZoom || 1, (formData.photoZoom || 1) * (formData.photoFlipX || 1));
        ctx.drawImage(img, -img.naturalWidth / 2, -img.naturalHeight / 2, img.naturalWidth, img.naturalHeight);
        ctx.restore();
        canvas.toBlob((blob) => resolve(blob), 'image/jpeg', 0.85);
      };
      img.onerror = () => resolve(null);
      img.src = formData.photo;
    });
  };

  // Save PRÓPRIO da foto (RN-113a), desacoplado do formulário: sobe a nova ou remove a
  // existente. Só para paciente já cadastrado (a key precisa do id).
  const handleSavePhoto = async () => {
    if (isNewPatient || !formData.id) {
      return;
    }
    setIsSavingPhoto(true);
    setErrors(prev => ({ ...prev, photo: '' }));
    try {
      if (formData.photo) {
        const blob = await createCroppedPhotoBlob();
        if (!blob) {
          throw new Error('Não foi possível processar a imagem.');
        }
        await patientsService.setPhoto(formData.id, blob);
        setServerHasPhoto(true);
      } else if (serverHasPhoto) {
        await patientsService.removePhoto(formData.id);
        setServerHasPhoto(false);
      }
      setPhotoDirty(false);
      setPhotoBlurred(true);
    } catch (error) {
      console.error('Erro ao salvar foto:', error);
      setErrors(prev => ({ ...prev, photo: 'Erro ao salvar a foto. Tente novamente.' }));
    } finally {
      setIsSavingPhoto(false);
    }
  };

  // Mudança na foto (novo arquivo, ajuste, remoção) marca "dirty" e revela para o usuário
  // conferir. Ignora no-op: o auto-fit do PhotoUpload dispara onPhotoChange ao carregar uma
  // foto do servidor (250x250 → zoom 1, sem mudança real) e não deve habilitar o salvar.
  const handlePhotoChange = (photoData: Partial<PatientFormData>) => {
    setFormData(prev => {
      const next = { ...prev, ...photoData };
      const changed =
        next.photo !== prev.photo ||
        next.photoRotation !== prev.photoRotation ||
        next.photoZoom !== prev.photoZoom ||
        next.photoFlipX !== prev.photoFlipX ||
        next.photoPositionX !== prev.photoPositionX ||
        next.photoPositionY !== prev.photoPositionY;
      if (changed) {
        setPhotoDirty(true);
        setPhotoBlurred(false);
      }
      return next;
    });
  };

  return (
    <div className="patient-register-page">
      <HeaderInternal
        showCTAButton={false}
        className=""
        isLoggedIn={true}
        userEmail={user?.email || ""}
        userProfile={user?.role || ""}
        clinicName={user?.clinicName || "Clínica"}
        notificationCount={27}
        onLogoClick={handleLogoClick}
        onRevalidateLogin={handleRevalidateLogin}
        onNotificationClick={handleNotificationClick}
        onUserClick={handleUserClick}
      />

      <main className="patient-register-main">
        {/* Título da página só no cadastro novo; o paciente existente é apresentado dentro do painel */}
        {isNewPatient && (
          <Box className="c4u-page-header c4u-page-header--card">
            <Box>
              <Typography variant="h4" className="c4u-page-header__title">Cadastro de Paciente</Typography>
              <Typography variant="body2" className="c4u-page-header__subtitle">
                Preencha os dados para cadastrar um novo paciente no sistema.
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FaqButton />
            </Box>
          </Box>
        )}

        <div className="patient-register-container">
          {!isNewPatient && (
            // Ações no canto superior direito em qualquer largura: no celular quem quebra linha é o
            // nome, não o bloco de ações (antes ele caía para baixo do nome e do ID)
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: { xs: 1, sm: 2 }, mb: 2 }}>
              <Box sx={{ minWidth: 0, flex: '1 1 auto', overflowWrap: 'anywhere' }}>
                <Typography sx={{ fontSize: '1.3rem', fontWeight: 700, color: '#212529', lineHeight: 1.2 }}>
                  {formData.name || 'Paciente'}
                </Typography>
                <Typography sx={{ fontSize: '0.8rem', color: colors.textSecondary, mt: 0.5 }}>
                  ID {formData.id}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: '0 0 auto' }}>
                {ageFromBirthDate(formData.birthDate) !== null && (
                  <Chip
                    size="small"
                    label={`${ageFromBirthDate(formData.birthDate)} anos`}
                    sx={{ bgcolor: '#e6f4f2', color: '#0d6e6e', fontWeight: 600 }}
                  />
                )}
                {isTestPatient && (
                  <Tooltip title="Paciente de teste: aparece na agenda e nas listas, mas fica fora de relatórios, do Dashboard e dos recebimentos.">
                    <Chip size="small" label="Teste" sx={{ bgcolor: '#fff3cd', color: '#856404', border: '1px solid #ffe08a', fontWeight: 700 }} />
                  </Tooltip>
                )}
                {canSetTestFlag && (
                  <Tooltip title={isTestPatient ? 'Desmarcar paciente de teste' : 'Marcar como paciente de teste (fora de relatórios e recebimentos)'}>
                    <FormControlLabel
                      sx={{ ml: 0.5, mr: 0, '& .MuiFormControlLabel-label': { fontSize: '0.78rem', color: colors.textSecondary } }}
                      control={<Switch size="small" checked={isTestPatient} disabled={savingTestFlag} onChange={(e) => toggleTestFlag(e.target.checked)} />}
                      label="Teste"
                    />
                  </Tooltip>
                )}
                {!isNewPatient && activeTab === 'cadastro' && (
                  <Tooltip title={privacyHidden ? 'Mostrar os dados do cadastro' : 'Ocultar os dados do cadastro na tela (só o nome fica visível)'}>
                    <IconButton
                      size="small"
                      onClick={() => setPrivacyHidden((v) => !v)}
                      aria-label={privacyHidden ? 'Mostrar dados do cadastro' : 'Ocultar dados do cadastro'}
                      sx={{ color: privacyHidden ? colors.primary : colors.textSecondary }}
                    >
                      {privacyHidden ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                  </Tooltip>
                )}
                <FaqButton />
              </Box>
            </Box>
          )}
          {/* Navegação entre abas. No celular vira um seletor: com 11 abas, o modo rolável
              obriga a varrer a faixa horizontalmente para achar a aba, e as que ficam fora
              da tela não são descobertas. Um toque abre a lista inteira (RN-539d). */}
          <Box sx={{ display: { xs: 'block', md: 'none' }, mb: 2 }}>
            <TextField
              select
              fullWidth
              size="small"
              label="Seção do cadastro"
              value={activeTab}
              onChange={(e) => handleTabChange(e.target.value)}
              InputLabelProps={getDefaultInputLabelProps()}
              // Sem trava de rolagem: no celular o MUI compensa a "barra de rolagem" com um
              // padding-right no body calculado de innerWidth - clientWidth; com a página
              // reduzida pelo zoom essa diferença vira centenas de px e esmaga o painel.
              SelectProps={{ MenuProps: { disableScrollLock: true } }}
            >
              {getAvailableTabs().map(tab => {
                const IconComponent = tab.icon;
                return (
                  <MenuItem key={tab.id} value={tab.id} disabled={!tab.enabled}>
                    <IconComponent sx={{ fontSize: '1rem', color: tab.color, mr: 1, verticalAlign: 'middle' }} />
                    {tab.label}
                  </MenuItem>
                );
              })}
            </TextField>
          </Box>

          <Box sx={{ display: { xs: 'none', md: 'block' }, borderBottom: '1px solid #e9ecef', mb: 2, pb: 1.5 }}>
            <Tabs
              value={activeTab}
              onChange={(e, newValue) => handleTabChange(newValue)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                minHeight: '40px',
                '& .MuiTab-root': {
                  textTransform: 'none',
                  minHeight: '40px',
                  fontSize: '1rem',
                  fontWeight: 500,
                  color: '#495057',
                  padding: '8px 16px',
                  marginRight: '8px',
                  minWidth: 'auto',
                  borderRadius: '999px',
                  '& .MuiSvgIcon-root': { color: 'inherit' },
                  '&.Mui-selected': {
                    color: '#fff',
                    backgroundColor: '#0d6e6e',
                    fontWeight: 600,
                  },
                },
                '& .MuiTabs-indicator': { display: 'none' },
                '& .MuiTabScrollButton-root': {
                  width: '30px',
                  padding: '0',
                },
              }}
            >
              {getAvailableTabs().map(tab => {
                const IconComponent = tab.icon;
                return (
                  <Tab
                    key={tab.id}
                    value={tab.id}
                    label={tab.label}
                    icon={<IconComponent sx={{ fontSize: '1rem' }} />}
                    iconPosition="start"
                    disabled={!tab.enabled}
                  />
                );
              })}
            </Tabs>
          </Box>

          {/* Conteúdo da aba ativa */}
          <div className="tab-content">
            {activeTab === 'cadastro' && (
              <form onSubmit={handleSubmit} className={`patient-register-form${privacyHidden ? " c4u-privacy-hidden" : ""}`}>
                {/* Seção: Dados Pessoais */}
                <div className="personal-data-layout">
                    {/* Seção de Upload de Foto */}
                    <div className="photo-upload-container">
                      <div style={{ position: 'relative', overflow: 'hidden', borderRadius: 8 }}>
                        <SavingOverlay open={isSavingPhoto} message="Salvando foto..." />
                        <PhotoUpload
                          photo={formData.photo}
                          photoRotation={formData.photoRotation}
                          photoZoom={formData.photoZoom}
                          photoFlipX={formData.photoFlipX}
                          photoPositionX={formData.photoPositionX}
                          photoPositionY={formData.photoPositionY}
                          blurred={photoBlurred}
                          onToggleBlur={() => setPhotoBlurred(prev => !prev)}
                          disabled={isNewPatient}
                          disabledMessage="Salve o cadastro para incluir a foto do paciente"
                          onPhotoChange={handlePhotoChange}
                        />
                      </div>

                      {/* Save PRÓPRIO da foto (RN-113a): independente do formulário */}
                      <Tooltip title={isNewPatient ? 'Salve o cadastro do paciente antes de enviar a foto' : ''}>
                        <span style={{ display: 'block' }}>
                          <Button
                            fullWidth
                            variant="contained"
                            onClick={handleSavePhoto}
                            disabled={isNewPatient || !photoDirty || isSavingPhoto}
                            sx={{ mt: 1, backgroundColor: colors.primary, textTransform: 'none', '&:hover': { backgroundColor: colors.primaryHover } }}
                          >
                            Salvar foto
                          </Button>
                        </span>
                      </Tooltip>
                      {errors.photo && (
                        <Typography sx={{ color: colors.error, fontSize: 13, mt: 0.5 }}>{errors.photo}</Typography>
                      )}

                    </div>

                    {/* Seção de Campos */}
                    <div className="personal-data-fields">
                      {/* Grid de migração - 3 colunas com bordas */}
                      <Box sx={{
                        display: 'grid',
                        gridTemplateColumns: {
                          xs: '1fr',
                          md: 'repeat(3, 1fr)'
                        },
                        columnGap: 1.5,
                        rowGap: 2,
                        mb: '16px',
                        '& > div': {
                          pt: 0,
                          px: 0,
                          pb: '5px'
                        }
                      }}>
                        <Box>
                          <TextField
                            className="c4u-privacy-keep"
                            fullWidth
                            size="small"
                            id="name"
                            name="name"
                            inputProps={{ maxLength: 100 }}
                            label="Nome Completo*"
                            value={formData.name}
                            onChange={handleInputChange}
                            placeholder="Nome completo do paciente"
                            disabled={!canEdit}
                            error={!!errors.name}
                            helperText={errors.name}
                            InputLabelProps={getDefaultInputLabelProps()}
                            sx={getDefaultTextFieldSx()}
                          />
                        </Box>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                          <TextField
                            fullWidth
                            size="small"
                            type="date"
                            id="birthDate"
                            name="birthDate"
                            label="Data de Nascimento*"
                            value={formData.birthDate}
                            onChange={handleInputChange}
                            disabled={!canEdit}
                            inputProps={{ max: todayIso() }}
                            error={!!errors.birthDate || isFutureBirthDate(formData.birthDate)}
                            helperText={errors.birthDate
                              || (isFutureBirthDate(formData.birthDate) ? 'A data não pode ser posterior a hoje' : '')}
                            InputLabelProps={getDefaultInputLabelProps()}
                            sx={getDefaultTextFieldSx()}
                            {...privacyMask()}
                          />
                          <TextField
                            fullWidth
                            size="small"
                            id="age"
                            name="age"
                            label="Idade"
                            value={calculateAge(formData.birthDate)}
                            disabled
                            placeholder="Calculado automaticamente"
                            InputLabelProps={getDefaultInputLabelProps()}
                            sx={{
                              '& .MuiInputBase-input': {
                                fontSize: '12px'
                              }
                            }}
                            {...privacyMask()}
                          />
                        </Box>
                        <Box>
                          <TextField
                            select
                            fullWidth
                            size="small"
                            id="gender"
                            name="gender"
                            label="Sexo biológico*"
                            value={formData.gender}
                            onChange={handleInputChange}
                            disabled={!canEdit}
                            InputLabelProps={getDefaultInputLabelProps()}
                            {...privacyMaskSelect()}
                          >
                            <MenuItem value="" disabled>Selecione</MenuItem>
                            {(listOptions?.genders || []).map(g => (
                              <MenuItem key={g.id} value={g.name}>{g.name}</MenuItem>
                            ))}
                            {/* Valor legado fora do catálogo (visualização) não some do select */}
                            {formData.gender && !(listOptions?.genders || []).some(g => g.name === formData.gender) && (
                              <MenuItem value={formData.gender}>{formData.gender}</MenuItem>
                            )}
                          </TextField>
                        </Box>
                      </Box>

                      {/* Segunda linha do grid de migração */}
                      <Box sx={{
                        display: 'grid',
                        gridTemplateColumns: {
                          xs: '1fr',
                          md: 'repeat(3, 1fr)'
                        },
                        columnGap: 1.5,
                        rowGap: 2,
                        mb: '16px',
                        '& > div': {
                          pt: 0,
                          px: 0,
                          pb: '5px'
                        }
                      }}>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                          <TextField
                            select
                            fullWidth
                            size="small"
                            id="documentType-new"
                            name="documentType"
                            label="Tipo de Documento*"
                            value={formData.documentType}
                            onChange={handleInputChange}
                            disabled={!canEdit}
                            InputLabelProps={getDefaultInputLabelProps()}
                            {...privacyMaskSelect()}
                            {...sensitiveLock('documents')}
                          >
                            {/* Tipos do catálogo do tenant (CPF/RG) — CNH/Passaporte não existem no sistema */}
                            {(listOptions?.documentTypes || [{ id: '', name: 'CPF', requiresExpedition: false, minLength: null, maxLength: null }]).map(d => (
                              <MenuItem key={d.id || d.name} value={d.name}>{d.name}</MenuItem>
                            ))}
                          </TextField>
                          <TextField
                            fullWidth
                            size="small"
                            id="document-new"
                            name="document"
                            inputProps={{ maxLength: 20 }}
                            label="Documento*"
                            value={formData.document}
                            onChange={handleInputChange}
                            placeholder="000.000.000-00"
                            disabled={!canEdit}
                            error={!!errors.document}
                            helperText={errors.document}
                            InputLabelProps={getDefaultInputLabelProps()}
                            {...privacyMask()}
                            {...sensitiveLock('documents')}
                          />
                        </Box>
                        <Box>
                          <TextField
                            fullWidth
                            size="small"
                            id="expeditorOrgan-new"
                            name="expeditorOrgan"
                            inputProps={{ maxLength: 30 }}
                            label="Órgão Expedidor"
                            value={formData.expeditorOrgan}
                            onChange={handleInputChange}
                            placeholder="SSP, DETRAN, etc."
                            disabled={!canEdit || !docTypeRequiresExpedition}
                            helperText={canEdit && !docTypeRequiresExpedition ? 'Aplicável apenas a RG' : undefined}
                            InputLabelProps={getDefaultInputLabelProps()}
                            sx={getDefaultTextFieldSx()}
                            {...privacyMask()}
                            {...sensitiveLock('documents')}
                          />
                        </Box>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                          <TextField
                            select
                            fullWidth
                            size="small"
                            id="originCountry-new"
                            name="originCountry"
                            label="País de Origem*"
                            value={formData.originCountry}
                            onChange={handleInputChange}
                            disabled={!canEdit}
                            InputLabelProps={getDefaultInputLabelProps()}
                            {...privacyMaskSelect()}
                          >
                            {/* Catálogo countries do tenant (relação no Bubble) — fallback Brasil até carregar
                                (ou enquanto a API no ar ainda não devolve countries no list-options) */}
                            {(listOptions?.countries?.length ? listOptions.countries : [{ id: '', name: 'Brasil' }]).map(c => (
                              <MenuItem key={c.id || c.name} value={c.name}>{c.name}</MenuItem>
                            ))}
                          </TextField>
                          <TextField
                            select
                            fullWidth
                            size="small"
                            id="nativeLanguage-new"
                            name="nativeLanguage"
                            label="Idioma Nativo*"
                            value={formData.nativeLanguage}
                            onChange={handleInputChange}
                            disabled={!canEdit}
                            InputLabelProps={getDefaultInputLabelProps()}
                            {...privacyMaskSelect()}
                          >
                            <MenuItem value="Português">Português</MenuItem>
                            <MenuItem value="Inglês">Inglês</MenuItem>
                            <MenuItem value="Espanhol">Espanhol</MenuItem>
                          </TextField>
                        </Box>
                      </Box>

                      {/* Terceira linha do grid de migração */}
                      <Box sx={{
                        display: 'grid',
                        gridTemplateColumns: {
                          xs: '1fr',
                          md: 'repeat(3, 1fr)'
                        },
                        columnGap: 1.5,
                        rowGap: 2,
                        mb: '16px',
                        '& > div': {
                          pt: '2px',
                          px: 0,
                          pb: '5px'
                        },
                        '& > div:nth-child(2), & > div:nth-child(3)': {
                          pt: '10px'
                        }
                      }}>
                        <Box>
                          <FormControl component="fieldset" error={!!errors.isResponsible}>
                            <FormLabel component="legend" sx={{ fontSize: '11px', color: '#6c757d', mb: 0.5, pt: 0 }}>
                              É o próprio responsável?*
                            </FormLabel>
                            <RadioGroup
                              row
                              name="isResponsible"
                              value={formData.isResponsible.toString()}
                              onChange={(e) => handleInputChange({
                                ...e,
                                target: { ...e.target, name: 'isResponsible', value: e.target.value }
                              })}
                            >
                              <FormControlLabel value="true" control={<Radio size="small" />} label="Sim" disabled={!canEdit} />
                              <FormControlLabel value="false" control={<Radio size="small" />} label="Não" disabled={!canEdit} />
                            </RadioGroup>
                            {errors.isResponsible && <Typography variant="caption" color="error">{errors.isResponsible}</Typography>}
                          </FormControl>
                        </Box>
                        <Box>
                          <PhoneWithCountryInput
                            id="phone-new"
                            label={`Telefone (Whatsapp)${formData.isResponsible ? '*' : ''}`}
                            value={formData.phone}
                            onChange={changePhone('phone')}
                            placeholder="11999999999"
                            error={!!errors.phone}
                            helperText={errors.phone}
                            disabled={!canEdit}
                            lockedProps={phoneLock()}
                          />
                        </Box>
                        <Box>
                          <TextField
                            fullWidth
                            size="small"
                            type="email"
                            id="email-new"
                            name="email"
                            inputProps={{ maxLength: 100 }}
                            label={`E-mail${formData.isResponsible ? '*' : ''}`}
                            value={formData.email}
                            onChange={handleInputChange}
                            placeholder="email@exemplo.com"
                            error={!!errors.email}
                            helperText={errors.email}
                            disabled={!canEdit}
                            InputLabelProps={getDefaultInputLabelProps()}
                            sx={getDefaultTextFieldSx()}
                            {...privacyMask()}
                            {...sensitiveLock('emails')}
                          />
                        </Box>
                      </Box>


                      {/* Responsáveis (condicional) */}
                      {!formData.isResponsible && (
                        <>
                          <div className="responsible-divider">
                            <h4>Responsáveis</h4>
                          </div>

                          {/* 1º Responsável - Grid de 3 colunas */}
                          <Box sx={{
                            display: 'grid',
                            gridTemplateColumns: {
                              xs: '1fr',
                              md: 'repeat(3, 1fr)'
                            },
                            columnGap: 1.5,
                            rowGap: 2,
                            mb: '16px',
                            '& > div': {
                              pt: 0,
                              px: 0,
                              pb: '5px'
                            }
                          }}>
                            <Box>
                              <TextField
                                fullWidth
                                size="small"
                                id="responsibleName-new"
                                name="responsibleName"
                                inputProps={{ maxLength: 100 }}
                                label="Nome Completo (1º Resp)*"
                                value={formData.responsibleName}
                                onChange={handleInputChange}
                                placeholder="Nome completo do responsável"
                                disabled={!canEdit}
                                InputLabelProps={getDefaultInputLabelProps()}
                                sx={getDefaultTextFieldSx()}
                                {...privacyMask()}
                              />
                            </Box>
                            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                              <TextField
                                fullWidth
                                size="small"
                                id="responsibleDocument-new"
                                name="responsibleDocument"
                                inputProps={{ maxLength: 20 }}
                                label="CPF"
                                value={formData.responsibleDocument}
                                onChange={handleInputChange}
                                placeholder="000.000.000-00"
                                disabled={!canEdit}
                                InputLabelProps={getDefaultInputLabelProps()}
                                sx={getDefaultTextFieldSx()}
                                {...privacyMask()}
                                {...sensitiveLock('documents')}
                              />
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    name="responsibleFinancial"
                                    checked={formData.responsibleFinancial}
                                    onChange={handleInputChange}
                                    size="small"
                                    disabled={!canEdit}
                                  />
                                }
                                label="Resp. Financeiro"
                                sx={{ whiteSpace: 'nowrap' }}
                              />
                            </Box>
                            <Box sx={{ display: 'flex', gap: 2 }}>
                              <PhoneWithCountryInput
                                id="responsiblePhone-new"
                                label="Telefone (Whatsapp)"
                                value={formData.responsiblePhone}
                                onChange={changePhone('responsiblePhone')}
                                placeholder="11999999999"
                                disabled={!canEdit}
                                lockedProps={phoneLock()}
                                sx={{ flex: '0 0 calc(50% + 2px)' }}
                              />
                              <TextField
                                fullWidth
                                size="small"
                                type="email"
                                id="responsibleEmail-new"
                                name="responsibleEmail"
                                inputProps={{ maxLength: 100 }}
                                label="E-mail"
                                value={formData.responsibleEmail}
                                onChange={handleInputChange}
                                placeholder="email@exemplo.com"
                                disabled={!canEdit}
                                InputLabelProps={getDefaultInputLabelProps()}
                                sx={getDefaultTextFieldSx()}
                                {...privacyMask()}
                                {...sensitiveLock('emails')}
                              />
                            </Box>
                          </Box>

                          {/* 2º Responsável - Grid de 3 colunas */}
                          <Box sx={{
                            display: 'grid',
                            gridTemplateColumns: {
                              xs: '1fr',
                              md: 'repeat(3, 1fr)'
                            },
                            columnGap: 1.5,
                            rowGap: 2,
                            mb: '16px',
                            '& > div': {
                              pt: 0,
                              px: 0,
                              pb: '5px'
                            }
                          }}>
                            <Box>
                              <TextField
                                fullWidth
                                size="small"
                                id="responsible2Name-new"
                                name="responsible2Name"
                                inputProps={{ maxLength: 100 }}
                                label="Nome Completo (2º Resp)"
                                value={formData.responsible2Name}
                                onChange={handleInputChange}
                                placeholder="Nome completo do 2º responsável"
                                disabled={!canEdit}
                                InputLabelProps={getDefaultInputLabelProps()}
                                sx={getDefaultTextFieldSx()}
                                {...privacyMask()}
                              />
                            </Box>
                            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                              <TextField
                                fullWidth
                                size="small"
                                id="responsible2Document-new"
                                name="responsible2Document"
                                inputProps={{ maxLength: 20 }}
                                label="CPF"
                                value={formData.responsible2Document}
                                onChange={handleInputChange}
                                placeholder="000.000.000-00"
                                disabled={!canEdit}
                                InputLabelProps={getDefaultInputLabelProps()}
                                sx={getDefaultTextFieldSx()}
                                {...privacyMask()}
                                {...sensitiveLock('documents')}
                              />
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    name="responsible2Financial"
                                    checked={formData.responsible2Financial}
                                    onChange={handleInputChange}
                                    size="small"
                                    disabled={!canEdit}
                                  />
                                }
                                label="Resp. Financeiro"
                                sx={{ whiteSpace: 'nowrap' }}
                              />
                            </Box>
                            <Box sx={{ display: 'flex', gap: 2 }}>
                              <PhoneWithCountryInput
                                id="responsible2Phone-new"
                                label="Telefone (Whatsapp)"
                                value={formData.responsible2Phone}
                                onChange={changePhone('responsible2Phone')}
                                placeholder="11999999999"
                                disabled={!canEdit}
                                lockedProps={phoneLock()}
                                sx={{ flex: '0 0 calc(50% + 2px)' }}
                              />
                              <TextField
                                fullWidth
                                size="small"
                                type="email"
                                id="responsible2Email-new"
                                name="responsible2Email"
                                inputProps={{ maxLength: 100 }}
                                label="E-mail"
                                value={formData.responsible2Email}
                                onChange={handleInputChange}
                                placeholder="email@exemplo.com"
                                disabled={!canEdit}
                                InputLabelProps={getDefaultInputLabelProps()}
                                sx={getDefaultTextFieldSx()}
                                {...privacyMask()}
                                {...sensitiveLock('emails')}
                              />
                            </Box>
                          </Box>
                        </>
                      )}

                      {/* Seção: Endereço */}
                      <div className="responsible-divider">
                        <h4>Endereço</h4>
                      </div>

                      {/* Primeira linha de Endereço - Grid de 3 colunas */}
                      <Box sx={{
                        display: 'grid',
                        gridTemplateColumns: {
                          xs: '1fr',
                          md: 'repeat(3, 1fr)'
                        },
                        columnGap: 1.5,
                        rowGap: 2,
                        mb: '16px',
                        '& > div': {
                          pt: 0,
                          px: 0,
                          pb: '5px'
                        }
                      }}>
                        <Box>
                          <TextField
                            fullWidth
                            size="small"
                            id="cep-new"
                            name="cep"
                            label="CEP*"
                            value={formatCep(formData.cep)}
                            onChange={(e) => {
                              const rawValue = e.target.value;
                              const cep = rawValue.replace(/\D/g, '');

                              // Permitir apenas 8 dígitos
                              if (cep.length <= 8) {
                                setFormData(prev => ({
                                  ...prev,
                                  cep: cep
                                }));

                                // Auto-busca quando CEP completo
                                if (cep.length === 8) {
                                  handleCepSearch(cep);
                                }
                              }
                            }}
                            placeholder="00000-000"
                            inputProps={{ maxLength: 9 }}
                            disabled={!canEdit}
                            InputLabelProps={getDefaultInputLabelProps()}
                            InputProps={{
                              endAdornment: cepLoading && <CircularProgress size={20} />
                            }}
                            {...privacyMask()}
                            {...sensitiveLock('addresses')}
                          />
                        </Box>
                        <Box>
                          <TextField
                            fullWidth
                            size="small"
                            id="address-new"
                            name="address"
                            inputProps={{ maxLength: 150 }}
                            label="Logradouro*"
                            value={formData.address}
                            onChange={handleInputChange}
                            placeholder="Rua, Avenida, etc."
                            disabled={!canEdit}
                            InputLabelProps={getDefaultInputLabelProps()}
                            sx={getDefaultTextFieldSx()}
                            {...privacyMask()}
                            {...sensitiveLock('addresses')}
                          />
                        </Box>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                          <TextField
                            fullWidth
                            size="small"
                            id="number-new"
                            name="number"
                            inputProps={{ maxLength: 10 }}
                            label="Número*"
                            value={formData.number}
                            onChange={handleInputChange}
                            placeholder="123"
                            disabled={!canEdit}
                            InputLabelProps={getDefaultInputLabelProps()}
                            sx={getDefaultTextFieldSx()}
                            {...privacyMask()}
                            {...sensitiveLock('addresses')}
                          />
                          <TextField
                            fullWidth
                            size="small"
                            id="complement-new"
                            name="complement"
                            inputProps={{ maxLength: 60 }}
                            label="Complemento"
                            value={formData.complement}
                            onChange={handleInputChange}
                            placeholder="Apto, Bloco, etc."
                            disabled={!canEdit}
                            InputLabelProps={getDefaultInputLabelProps()}
                            sx={getDefaultTextFieldSx()}
                            {...privacyMask()}
                            {...sensitiveLock('addresses')}
                          />
                        </Box>
                      </Box>

                      {/* Segunda linha de Endereço - Grid de 3 colunas */}
                      <Box sx={{
                        display: 'grid',
                        gridTemplateColumns: {
                          xs: '1fr',
                          md: 'repeat(3, 1fr)'
                        },
                        columnGap: 1.5,
                        rowGap: 2,
                        mb: '16px',
                        '& > div': {
                          pt: 0,
                          px: 0,
                          pb: '5px'
                        }
                      }}>
                        <Box>
                          <TextField
                            fullWidth
                            size="small"
                            id="neighborhood-new"
                            name="neighborhood"
                            inputProps={{ maxLength: 60 }}
                            label="Bairro*"
                            value={formData.neighborhood}
                            onChange={handleInputChange}
                            placeholder="Nome do bairro"
                            disabled={!canEdit}
                            InputLabelProps={getDefaultInputLabelProps()}
                            sx={getDefaultTextFieldSx()}
                            {...privacyMask()}
                            {...sensitiveLock('addresses')}
                          />
                        </Box>
                        <Box>
                          <TextField
                            fullWidth
                            size="small"
                            id="city-new"
                            name="city"
                            inputProps={{ maxLength: 60 }}
                            label="Cidade*"
                            value={formData.city}
                            onChange={handleInputChange}
                            placeholder="Nome da cidade"
                            disabled={!canEdit}
                            InputLabelProps={getDefaultInputLabelProps()}
                            sx={getDefaultTextFieldSx()}
                            {...privacyMask()}
                            {...sensitiveLock('addresses')}
                          />
                        </Box>
                        <Box>
                          <TextField
                            select
                            fullWidth
                            size="small"
                            id="uf-new"
                            name="uf"
                            inputProps={{ maxLength: 2 }}
                            label="UF*"
                            value={formData.uf}
                            onChange={handleInputChange}
                            disabled={!canEdit}
                            InputLabelProps={getDefaultInputLabelProps()}
                            {...privacyMaskSelect()}
                            {...sensitiveLock('addresses')}
                          >
                            <MenuItem value="" disabled>Selecione</MenuItem>
                            <MenuItem value="AC">AC</MenuItem>
                            <MenuItem value="AL">AL</MenuItem>
                            <MenuItem value="AP">AP</MenuItem>
                            <MenuItem value="AM">AM</MenuItem>
                            <MenuItem value="BA">BA</MenuItem>
                            <MenuItem value="CE">CE</MenuItem>
                            <MenuItem value="DF">DF</MenuItem>
                            <MenuItem value="ES">ES</MenuItem>
                            <MenuItem value="GO">GO</MenuItem>
                            <MenuItem value="MA">MA</MenuItem>
                            <MenuItem value="MT">MT</MenuItem>
                            <MenuItem value="MS">MS</MenuItem>
                            <MenuItem value="MG">MG</MenuItem>
                            <MenuItem value="PA">PA</MenuItem>
                            <MenuItem value="PB">PB</MenuItem>
                            <MenuItem value="PR">PR</MenuItem>
                            <MenuItem value="PE">PE</MenuItem>
                            <MenuItem value="PI">PI</MenuItem>
                            <MenuItem value="RJ">RJ</MenuItem>
                            <MenuItem value="RN">RN</MenuItem>
                            <MenuItem value="RS">RS</MenuItem>
                            <MenuItem value="RO">RO</MenuItem>
                            <MenuItem value="RR">RR</MenuItem>
                            <MenuItem value="SC">SC</MenuItem>
                            <MenuItem value="SP">SP</MenuItem>
                            <MenuItem value="SE">SE</MenuItem>
                            <MenuItem value="TO">TO</MenuItem>
                          </TextField>
                        </Box>
                      </Box>

                      {/* Observações ocupando 3 colunas dentro da seção de endereço */}
                      <div className="responsible-divider">
                        <h4>Observações</h4>
                      </div>

                      {/* Grid de Observações - 1 coluna ocupando o tamanho de 3 colunas */}
                      <Box sx={{
                        display: 'grid',
                        gridTemplateColumns: {
                          xs: '1fr',
                          md: 'repeat(3, 1fr)'
                        },
                        columnGap: 1.5,
                        rowGap: 2,
                        mb: '16px',
                        '& > div': {
                          pt: 0,
                          px: 0,
                          pb: '5px'
                        }
                      }}>
                        <Box sx={{ gridColumn: 'span 3' }}>
                          <TextField
                            label="Observações Gerais (internas)"
                            fullWidth
                            multiline
                            rows={3}
                            name="observations"
                            inputProps={{ maxLength: 2000 }}
                            value={formData.observations}
                            onChange={handleInputChange}
                            placeholder="Observações sobre o paciente"
                            disabled={!canEdit}
                            InputLabelProps={{
                              shrink: inputs.multiline.labelShrink,
                              sx: {
                                fontSize: inputs.multiline.labelFontSize,
                                color: inputs.multiline.labelColor,
                                backgroundColor: inputs.multiline.labelBackground,
                                padding: inputs.multiline.labelPadding,
                                '&.Mui-focused': {
                                  color: colors.primary,
                                },
                              },
                            }}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                position: inputs.multiline.position,
                                opacity: inputs.multiline.opacity,
                                alignItems: inputs.multiline.alignItems,
                                fontSize: inputs.multiline.fontSize,
                                minHeight: inputs.multiline.minHeight,
                                maxHeight: inputs.multiline.maxHeight,
                                overflow: inputs.multiline.overflow,
                                padding: 0,
                                '& fieldset': {
                                  borderColor: inputs.multiline.borderColor,
                                },
                                '&:hover fieldset': {
                                  borderColor: inputs.multiline.borderColor,
                                },
                                '& textarea': {
                                  wordWrap: inputs.multiline.wordWrap,
                                  whiteSpace: inputs.multiline.whiteSpace,
                                  padding: inputs.multiline.inputPadding,
                                  height: inputs.multiline.textareaHeight,
                                  maxHeight: inputs.multiline.textareaMaxHeight,
                                  overflow: `${inputs.multiline.textareaOverflow} !important`,
                                  boxSizing: inputs.multiline.textareaBoxSizing,
                                  '&::-webkit-scrollbar': {
                                    width: inputs.multiline.scrollbarWidth,
                                  },
                                  '&::-webkit-scrollbar-track': {
                                    backgroundColor: inputs.multiline.scrollbarTrackColor,
                                  },
                                  '&::-webkit-scrollbar-thumb': {
                                    backgroundColor: inputs.multiline.scrollbarThumbColor,
                                    borderRadius: '4px',
                                    '&:hover': {
                                      backgroundColor: inputs.multiline.scrollbarThumbHoverColor,
                                    },
                                  },
                                },
                              },
                            }}
                            {...privacyMask()}
                          />
                        </Box>

                        <Box sx={{ gridColumn: 'span 3' }}>
                          <TextField
                            label={hasPatientNotes
                              ? "Observações do paciente/responsável (preenchida)"
                              : "Observações do paciente/responsável"}
                            fullWidth
                            multiline
                            rows={3}
                            value={showPatientNotes
                              ? (formData.patientNotes || '')
                              : (hasPatientNotes ? PATIENT_NOTES_MASK : '')}
                            placeholder="Nenhuma observação enviada pelo paciente/responsável"
                            InputProps={{
                              readOnly: true,
                              endAdornment: (
                                <InputAdornment position="end" sx={{ alignSelf: 'flex-start', mt: '10px', mr: '4px' }}>
                                  <Tooltip title={showPatientNotes ? 'Ocultar' : 'Visualizar'} arrow>
                                    {/* span evita que o Tooltip perca o alvo quando o botão está desabilitado */}
                                    <span>
                                      <IconButton
                                        size="small"
                                        onClick={() => setShowPatientNotes((v) => !v)}
                                        disabled={!hasPatientNotes}
                                        aria-label={showPatientNotes
                                          ? 'Ocultar observações do paciente'
                                          : 'Visualizar observações do paciente'}
                                        sx={{ padding: '2px' }}
                                      >
                                        {showPatientNotes
                                          ? <VisibilityOff sx={{ fontSize: '1.1rem', color: '#6c757d' }} />
                                          : <Visibility sx={{ fontSize: '1.1rem', color: hasPatientNotes ? colors.primary : '#adb5bd' }} />}
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                </InputAdornment>
                              ),
                            }}
                            helperText={hasPatientNotes
                              ? 'Escrito pelo paciente/responsável no formulário de cadastro — somente leitura'
                              : 'O paciente/responsável ainda não enviou observações'}
                            InputLabelProps={{
                              shrink: inputs.multiline.labelShrink,
                              sx: {
                                fontSize: inputs.multiline.labelFontSize,
                                color: hasPatientNotes ? colors.primary : inputs.multiline.labelColor,
                                fontWeight: hasPatientNotes ? 600 : undefined,
                                backgroundColor: inputs.multiline.labelBackground,
                                padding: inputs.multiline.labelPadding,
                                '&.Mui-focused': {
                                  color: colors.primary,
                                },
                              },
                            }}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                position: inputs.multiline.position,
                                opacity: inputs.multiline.opacity,
                                alignItems: inputs.multiline.alignItems,
                                fontSize: inputs.multiline.fontSize,
                                minHeight: inputs.multiline.minHeight,
                                maxHeight: inputs.multiline.maxHeight,
                                overflow: inputs.multiline.overflow,
                                padding: 0,
                                backgroundColor: '#f8f9fa',
                                '& fieldset': {
                                  borderColor: inputs.multiline.borderColor,
                                },
                                '&:hover fieldset': {
                                  borderColor: inputs.multiline.borderColor,
                                },
                                '& textarea': {
                                  wordWrap: inputs.multiline.wordWrap,
                                  whiteSpace: inputs.multiline.whiteSpace,
                                  padding: inputs.multiline.inputPadding,
                                  height: inputs.multiline.textareaHeight,
                                  maxHeight: inputs.multiline.textareaMaxHeight,
                                  overflow: `${inputs.multiline.textareaOverflow} !important`,
                                  boxSizing: inputs.multiline.textareaBoxSizing,
                                  '&::-webkit-scrollbar': {
                                    width: inputs.multiline.scrollbarWidth,
                                  },
                                  '&::-webkit-scrollbar-track': {
                                    backgroundColor: inputs.multiline.scrollbarTrackColor,
                                  },
                                  '&::-webkit-scrollbar-thumb': {
                                    backgroundColor: inputs.multiline.scrollbarThumbColor,
                                    borderRadius: '4px',
                                    '&:hover': {
                                      backgroundColor: inputs.multiline.scrollbarThumbHoverColor,
                                    },
                                  },
                                },
                              },
                            }}
                            {...privacyMask()}
                          />
                        </Box>

                      </Box>

                      {/* Grid: Canal de entrada, Indicado por e Status */}
                      <Box sx={{
                        display: 'grid',
                        gridTemplateColumns: {
                          xs: '1fr',
                          md: 'repeat(3, 1fr)'
                        },
                        columnGap: 1.5,
                        rowGap: 2,
                        mb: '16px',
                        '& > div': {
                          pt: '2px',
                          px: 0,
                          pb: '5px'
                        },
                        '& > div:nth-child(1), & > div:nth-child(2)': {
                          pt: '10px'
                        }
                      }}>
                        <Box>
                          <TextField
                            select
                            fullWidth
                            size="small"
                            id="entryChannel"
                            name="entryChannel"
                            label="Canal de entrada"
                            value={formData.entryChannel}
                            onChange={handleInputChange}
                            disabled={!canEdit}
                            InputLabelProps={getDefaultInputLabelProps()}
                            SelectProps={{
                              MenuProps: {
                                PaperProps: {
                                  sx: {
                                    backgroundColor: 'white',
                                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                                  },
                                },
                              },
                            }}
                            {...privacyMaskSelect()}
                          >
                            <MenuItem value="">Selecione</MenuItem>
                            <MenuItem value="Instagram">Instagram</MenuItem>
                            <MenuItem value="Facebook">Facebook</MenuItem>
                            <MenuItem value="Google">Google</MenuItem>
                            <MenuItem value="Site">Site</MenuItem>
                            <MenuItem value="Indicação profissional">Indicação profissional</MenuItem>
                            <MenuItem value="Indicação de conhecido">Indicação de conhecido</MenuItem>
                            <MenuItem value="Outros">Outros</MenuItem>
                          </TextField>
                        </Box>

                        <Box>
                          <TextField
                            fullWidth
                            size="small"
                            id="referredBy"
                            name="referredBy"
                            inputProps={{ maxLength: 150 }}
                            label="Indicado por"
                            value={formData.referredBy || ''}
                            onChange={handleInputChange}
                            placeholder="Nome da pessoa ou instituição que indicou o paciente"
                            disabled={!canEdit}
                            InputLabelProps={getDefaultInputLabelProps()}
                            sx={getDefaultTextFieldSx()}
                            {...privacyMask()}
                          />
                        </Box>

                        <Box>
                          <FormControl component="fieldset">
                            <FormLabel component="legend" sx={{ fontSize: '11px', color: '#6c757d', mb: 0.5, pt: 0 }}>
                              Status do Cadastro
                            </FormLabel>
                            <RadioGroup
                              row
                              name="isComplete"
                              value={formData.isComplete.toString()}
                              onChange={(e) => handleInputChange({
                                ...e,
                                target: { ...e.target, name: 'isComplete', value: e.target.value }
                              })}
                            >
                              <FormControlLabel value="true" control={<Radio size="small" />} label="Completo" disabled={!canEdit} />
                              <FormControlLabel value="false" control={<Radio size="small" />} label="Incompleto" disabled={!canEdit} />
                            </RadioGroup>
                          </FormControl>
                        </Box>
                      </Box>

                      {/* Linha demarcatória turquesa entre observações e botões */}
                      <div className="form-divider-line"></div>

                      {/* Nova linha com 3 colunas para os botões */}
                      <div className="form-row">
                        <div className="form-group">
                          {/* Primeira coluna vazia */}
                        </div>
                        <div className="form-group">
                          {/* Segunda coluna vazia */}
                        </div>
                        <div className="form-group">
                          {errors.general && (
                            <Typography sx={{ color: colors.error, fontSize: 14, mb: 1, textAlign: 'right' }}>
                              {errors.general}
                            </Typography>
                          )}
                          <Box sx={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                            {isNewPatient || isEditing ? (
                              <>
                                <Button
                                  variant="outlined"
                                  onClick={handleCancel}
                                  sx={{
                                    color: '#6c757d',
                                    borderColor: '#6c757d',
                                    textTransform: 'none',
                                    fontSize: '0.875rem',
                                    fontWeight: 600,
                                    padding: '0.5rem 1.5rem',
                                    '&:hover': {
                                      borderColor: '#5a6268',
                                      backgroundColor: 'rgba(108, 117, 125, 0.04)',
                                    },
                                  }}
                                >
                                  Cancelar
                                </Button>
                                <Button
                                  type="submit"
                                  variant="contained"
                                  disabled={isLoading}
                                  sx={{
                                    backgroundColor: '#03B4C6',
                                    color: '#ffffff',
                                    textTransform: 'none',
                                    fontSize: '0.875rem',
                                    fontWeight: 600,
                                    padding: '0.5rem 1.5rem',
                                    boxShadow: 'none',
                                    '&:hover': {
                                      backgroundColor: '#029AAB',
                                      boxShadow: 'none',
                                    },
                                    '&:disabled': {
                                      backgroundColor: '#ced4da',
                                      color: '#ffffff',
                                    },
                                  }}
                                >
                                  {isLoading ? "Salvando..." : "Salvar"}
                                </Button>
                              </>
                            ) : (
                              <Button
                                variant="contained"
                                onClick={handleEdit}
                                disabled={!canEditRegister}
                                title={canEditRegister ? undefined : 'O seu perfil não edita o cadastro'}
                                sx={{
                                  backgroundColor: '#03B4C6',
                                  color: '#ffffff',
                                  textTransform: 'none',
                                  fontSize: '0.875rem',
                                  fontWeight: 600,
                                  padding: '0.5rem 1.5rem',
                                  boxShadow: 'none',
                                  '&:hover': {
                                    backgroundColor: '#029AAB',
                                    boxShadow: 'none',
                                  },
                                }}
                              >
                                Editar
                              </Button>
                            )}
                          </Box>
                        </div>
                      </div>
                    </div>
                </div>
              </form>
            )}

            {/* Visao geral (RN-529): painel de situacao. Passa as abas navegaveis para o card so
                virar link quando a tela de destino existir de verdade. */}
            {activeTab === 'resumo' && (
              <Box sx={{ p: 2, pt: '6px', px: '11px' }}>
                <PatientOverview
                  patientId={getParam('id') || ''}
                  onNavigate={handleTabChange}
                  navigableTabs={getAvailableTabs().map(t => t.id).filter(id => REAL_TABS.includes(id))}
                />
              </Box>
            )}

            {/* Conteúdo da aba Anotações */}
            {activeTab === 'linha-do-tempo' && (
              <PatientTimeline
                patientId={getParam('id') || ''}
                entityId={user?.entityId || ''}
              />
            )}

            {/* Anotações gerais (RN-534): lista real + modal (criar/editar) e exclusão */}
            {activeTab === 'anotacoes' && (
              <PatientNotesTab patientId={getParam('id') || ''} />
            )}

            {/* Vínculos (RN-553): escola, trabalho, esporte, instituição — com vigência */}
            {activeTab === 'vinculos' && (
              <PatientLinksTab patientId={getParam('id') || ''} />
            )}

            {/* Agenda do paciente (RN-533): agendamentos reais + modal da agenda (criar/editar) */}
            {activeTab === 'agenda' && (
              <PatientScheduleTab
                patientId={getParam('id') || ''}
                patientName={formData.name}
                entityId={user?.entityId || ''}
              />
            )}

            {/* Conteúdo da aba Diagnóstico */}
            {/* Diagnóstico (RN-535): CIDs reais — permissão própria patient-diagnosis */}
            {activeTab === 'diagnostico' && (
              <PatientDiagnosisTab patientId={getParam('id') || ''} />
            )}

            {/* Avaliações (RN-537): aplicações reais dos formulários — permissão patient-assessments */}
            {activeTab === 'avaliacoes' && (
              <PatientAssessmentsTab patientId={getParam('id') || ''} />
            )}

            {/* Conteúdo da aba Plano Terapêutico */}
            {activeTab === 'plano-terap' && (
              <PatientTherapyPlanTab patientId={getParam('id') || ''} />
            )}

            {/* Evoluções: registros reais por sessão — permissão própria patient-evolutions */}
            {activeTab === 'evolucoes' && (
              <PatientEvolutionsTab patientId={getParam('id') || ''} />
            )}

            {/* Conteúdo da aba Financeiro */}
            {activeTab === 'financeiro' && (
              <PatientFinancialTab patientId={getParam('id') || ''} />
            )}

            {/* Conteúdo da aba Receituário */}
            {/* Documentos (RN-539; antes Receituário): etapa 1 — layout no padrão das abas; amostra até a API */}
            {activeTab === 'receituario' && (
              <PatientDocumentsTab patientId={getParam('id') || ''} />
            )}

            {/* Contratos (RN-558): emitidos a partir dos modelos versionados — permissão patient-contracts */}
            {activeTab === 'contratos' && (
              <PatientContractsTab patientId={getParam('id') || ''} />
            )}

            {/* Conteúdo da aba Arquivos */}
            {/* Arquivos (RN-538): anexos reais no MinIO — permissão patient-files */}
            {activeTab === 'arquivos' && (
              <PatientFilesTab patientId={getParam('id') || ''} />
            )}

          </div>
        </div>
      </main>

      <Toast message={toast.message} type={toast.type} isVisible={toast.isVisible} onClose={hideToast} />
      <FooterInternal
        className="login-footer-component"
      />

      {/* Modal de Inserção/Edição de Avaliação */}
      <Dialog
        open={isEvaluationModalOpen}
        onClose={handleCloseEvaluationModal}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
            maxHeight: '90vh',
          }
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: colors.primary,
            color: colors.white,
            padding: '1.5rem',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography
            variant="h6"
            component="h3"
            sx={{
              fontSize: '1.4rem',
              fontWeight: typography.fontWeight.semibold,
              margin: 0
            }}
          >
            {currentEvaluation ? 'Editar Avaliação' : 'Nova Avaliação'}
          </Typography>
          <IconButton
            onClick={handleCloseEvaluationModal}
            sx={{
              color: colors.white,
              padding: '0.25rem',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)'
              }
            }}
          >
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ padding: '1.5rem !important', paddingTop: '2rem !important' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            {/* Formulário */}
            <Autocomplete
              options={formList}
              value={evaluationFormData.form}
              onChange={(event, newValue) => {
                setEvaluationFormData(prev => ({ ...prev, form: newValue || '' }));
              }}
              disabled={!!currentEvaluation}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Formulário"
                  required
                  placeholder="Selecione um formulário"
                  InputLabelProps={{
                    shrink: true,
                    sx: {
                      fontSize: inputs.default.labelFontSize,
                      color: inputs.default.labelColor,
                      backgroundColor: inputs.default.labelBackground,
                      padding: inputs.default.labelPadding,
                      '&.Mui-focused': {
                        color: inputs.default.focus.labelColor,
                      },
                    },
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      height: inputs.default.height,
                      fontSize: inputs.default.fontSize,
                      backgroundColor: currentEvaluation ? '#f5f5f5' : '#fff',
                      '& fieldset': {
                        borderColor: inputs.default.borderColor,
                      },
                      '&:hover fieldset': {
                        borderColor: inputs.default.hover.borderColor,
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: inputs.default.focus.borderColor,
                        boxShadow: inputs.default.focus.boxShadow,
                      },
                    },
                  }}
                />
              )}
            />

            {/* Observações */}
            <TextField
              label="Observações"
              value={evaluationFormData.observations}
              onChange={(e) => setEvaluationFormData(prev => ({ ...prev, observations: e.target.value }))}
              placeholder="Digite observações sobre a avaliação"
              multiline
              rows={4}
              InputLabelProps={{
                shrink: inputs.multiline.labelShrink,
                sx: {
                  fontSize: inputs.multiline.labelFontSize,
                  color: inputs.multiline.labelColor,
                  backgroundColor: inputs.multiline.labelBackground,
                  padding: inputs.multiline.labelPadding,
                  '&.Mui-focused': {
                    color: colors.primary,
                  },
                },
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  position: inputs.multiline.position,
                  opacity: inputs.multiline.opacity,
                  alignItems: inputs.multiline.alignItems,
                  fontSize: inputs.multiline.fontSize,
                  minHeight: inputs.multiline.minHeight,
                  maxHeight: inputs.multiline.maxHeight,
                  overflow: inputs.multiline.overflow,
                  padding: 0,
                  '& fieldset': {
                    borderColor: inputs.multiline.borderColor,
                  },
                  '&:hover fieldset': {
                    borderColor: inputs.multiline.borderColor,
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: colors.primary,
                  },
                  '& textarea': {
                    wordWrap: inputs.multiline.wordWrap,
                    whiteSpace: inputs.multiline.whiteSpace,
                    padding: inputs.multiline.inputPadding,
                    height: inputs.multiline.textareaHeight,
                    maxHeight: inputs.multiline.textareaMaxHeight,
                    overflow: `${inputs.multiline.textareaOverflow} !important`,
                    boxSizing: inputs.multiline.textareaBoxSizing,
                    '&::-webkit-scrollbar': {
                      width: inputs.multiline.scrollbarWidth,
                    },
                    '&::-webkit-scrollbar-track': {
                      backgroundColor: inputs.multiline.scrollbarTrackColor,
                    },
                    '&::-webkit-scrollbar-thumb': {
                      backgroundColor: inputs.multiline.scrollbarThumbColor,
                      borderRadius: '4px',
                      '&:hover': {
                        backgroundColor: inputs.multiline.scrollbarThumbHoverColor,
                      },
                    },
                  },
                },
              }}
            />

            {/* Prazo para preenchimento */}
            <TextField
              type="date"
              label="Prazo para Preenchimento"
              required
              value={evaluationFormData.deadline}
              onChange={(e) => setEvaluationFormData(prev => ({ ...prev, deadline: e.target.value }))}
              InputLabelProps={{
                shrink: true,
                sx: {
                  fontSize: inputs.default.labelFontSize,
                  color: inputs.default.labelColor,
                  backgroundColor: inputs.default.labelBackground,
                  padding: inputs.default.labelPadding,
                  '&.Mui-focused': {
                    color: inputs.default.focus.labelColor,
                  },
                },
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  height: inputs.default.height,
                  fontSize: inputs.default.fontSize,
                  '& fieldset': {
                    borderColor: inputs.default.borderColor,
                  },
                  '&:hover fieldset': {
                    borderColor: inputs.default.hover.borderColor,
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: inputs.default.focus.borderColor,
                    boxShadow: inputs.default.focus.boxShadow,
                  },
                },
              }}
            />
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            padding: '1.5rem 2rem',
            borderTop: `1px solid ${colors.backgroundAlt}`,
            backgroundColor: colors.background,
            gap: '1rem',
          }}
        >
          <Button
            onClick={handleCloseEvaluationModal}
            variant="outlined"
            sx={{
              padding: '0.75rem 1.5rem',
              border: `1px solid ${colors.border}`,
              borderRadius: '6px',
              backgroundColor: colors.white,
              color: colors.textSecondary,
              fontSize: '1rem',
              fontWeight: typography.fontWeight.medium,
              textTransform: 'none',
              '&:hover': {
                backgroundColor: colors.background,
                borderColor: '#adb5bd',
              }
            }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSaveEvaluation}
            variant="contained"
            sx={{
              padding: '0.75rem 1.5rem',
              borderRadius: '6px',
              backgroundColor: colors.primary,
              color: colors.white,
              fontSize: '1rem',
              fontWeight: typography.fontWeight.medium,
              textTransform: 'none',
              boxShadow: 'none',
              '&:hover': {
                backgroundColor: '#029AAB',
                boxShadow: 'none',
                transform: 'translateY(-1px)',
              }
            }}
          >
            Salvar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Exclusão de Avaliação */}
      <Dialog
        open={isDeleteEvaluationModalOpen}
        onClose={handleCloseDeleteEvaluationModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
            maxHeight: '90vh',
          }
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: colors.error,
            color: colors.white,
            padding: '1.5rem',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography
            variant="h6"
            component="h3"
            sx={{
              fontSize: '1.4rem',
              fontWeight: typography.fontWeight.semibold,
              margin: 0
            }}
          >
            Confirmar Exclusão
          </Typography>
          <IconButton
            onClick={handleCloseDeleteEvaluationModal}
            sx={{
              color: colors.white,
              padding: '0.25rem',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)'
              }
            }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ padding: '2rem !important' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <Typography variant="body1" sx={{ fontSize: '1.1rem', color: colors.text, lineHeight: 1.6 }}>
              Tem certeza que deseja excluir a avaliação <strong>{currentEvaluation?.form}</strong>?
            </Typography>
            <Box sx={{ p: 1.5, backgroundColor: '#fff3cd', borderRadius: '8px', border: '1px solid #ffeaa7' }}>
              <Typography variant="body2" sx={{ fontSize: '0.875rem', color: '#856404', lineHeight: 1.6 }}>
                <strong>Atenção:</strong> Esta ação não poderá ser desfeita.
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ padding: '1.5rem', gap: '0.75rem', justifyContent: 'flex-end' }}>
          <Button
            onClick={handleCloseDeleteEvaluationModal}
            variant="outlined"
            sx={{
              borderColor: colors.textSecondary,
              color: colors.textSecondary,
              fontSize: '1rem',
              fontWeight: typography.fontWeight.medium,
              textTransform: 'none',
              boxShadow: 'none',
              '&:hover': {
                borderColor: colors.text,
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                boxShadow: 'none',
              }
            }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleDeleteEvaluation}
            variant="contained"
            sx={{
              backgroundColor: colors.error,
              color: colors.white,
              fontSize: '1rem',
              fontWeight: typography.fontWeight.medium,
              textTransform: 'none',
              boxShadow: 'none',
              '&:hover': {
                backgroundColor: '#c82333',
                boxShadow: 'none',
                transform: 'translateY(-1px)',
              }
            }}
          >
            Excluir
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default PatientRegister;